/** @jsxImportSource @emotion/react */
import { Formik, FormikConfig, FormikProps } from 'formik';
import UDFormInput from 'ud-form/components/input';
import { modalButton, modalError, modalInputStyle } from '../modals/styles';
import { Button } from 'ud-ui/components/button/styles';
import classNames from 'classnames';
import React from 'react';
import _ from 'lodash';
import { Values } from '../../../../domain/interface/form/template';
import SizeInputs from 'modules/template-editor/ui/components/elements/components/size/size-inputs';
import UDFormSelect from 'ud-form/components/select';
import { accessOptions, visibilityOptions } from '../../../../domain/consts/form/template/options';
import Row from 'modules/template-editor/ui/components/elements/components/events-selector/row';
import { CustomSelect } from 'modules/template-editor/ui/components/elements/components/events-selector/custom-select';
import { templatePresets, TemplatePresetsNameList } from '../../../../domain/consts/form/template/template-presets';
import Modal from 'ud-ui/components/modal';
import * as yup from 'yup';
import templateNameValidator from 'modules/template-editor/domain/validators/template-name-validator';
import widthValidator from 'modules/template-editor/domain/validators/width.validator';
import heightValidator from 'modules/template-editor/domain/validators/height.validator';

const templateValidator = (existingNames: string[]) =>
  yup.object({
    name: yup
      .string()
      .transform((val) => (typeof val === 'string' ? val.trim() : val))
      .test('template-name', 'Invalid name', function (value) {
        const error = templateNameValidator(value ?? '', existingNames);
        if (error) {
          return this.createError({ message: error });
        }
        return true;
      }),
    width: yup
      .string()
      .required('Введите ширину')
      .test('template-width', 'Некорректная ширина', function (value) {
        const num = value === undefined || value === null || value === '' ? NaN : Number(value);
        const error = widthValidator(num as any);
        if (error) {
          return this.createError({ message: error });
        }
        return true;
      }),
    height: yup
      .string()
      .required('Введите высоту')
      .test('template-height', 'Некорректная высота', function (value) {
        const num = value === undefined || value === null || value === '' ? NaN : Number(value);
        const error = heightValidator(num as any);
        if (error) {
          return this.createError({ message: error });
        }
        return true;
      }),
  });

type IProps = FormikConfig<Values> & {
  advancedAccess: boolean;
  isLoading: boolean;
  preset: TemplatePresetsNameList;
  existingNames: string[];
};

function TemplateForm(props: IProps) {
  const { advancedAccess, isLoading, preset, existingNames, ...rest } = props;

  const getErrors = (props: FormikProps<Values>) => {
    return _.find(props.errors, (error, key) => !!error && !!props.touched[key]);
  };

  const text = templatePresets[preset];

  return (
    <Formik validateOnChange validationSchema={templateValidator(existingNames)} {...rest}>
      {(formProps) => {
        return (
          <form onSubmit={formProps.handleSubmit} className="w-[450px]">
            <Modal.Body className="flex-column gap-5">
              <UDFormInput
                readOnly={isLoading}
                onChangeCapture={() => {
                  formProps.values.name && !formProps.touched.name && formProps.setFieldTouched('name');
                }}
                name={'name'}
                autoFocus={true}
                css={modalInputStyle}
                placeholder={'Название шаблона'}
              />
              <SizeInputs readonly={isLoading} />
              {advancedAccess && (
                <>
                  <Row headerText={'Тип шаблона'}>
                    <UDFormSelect
                      containerProps={{ style: { width: '100%' } }}
                      selectComponent={CustomSelect}
                      name={'access'}
                      options={accessOptions}
                    />
                  </Row>
                  <Row headerText={'Видимость'}>
                    <UDFormSelect
                      containerProps={{ style: { width: '100%' } }}
                      selectComponent={CustomSelect}
                      name={'visibility'}
                      options={visibilityOptions}
                    />
                  </Row>
                </>
              )}
            </Modal.Body>
            <Modal.Footer justify="start">
              <Button
                type={'submit'}
                className={classNames('flex-center', isLoading && 'loading')}
                disabled={!formProps.isValid || formProps.values.name === ''}
                css={modalButton}
              >
                {text.submitButton}
              </Button>
              <span css={modalError}>{getErrors(formProps)}</span>
            </Modal.Footer>
          </form>
        );
      }}
    </Formik>
  );
}

export default TemplateForm;
